// src/frontend_react/src/pages/UserDashboard/ResultsTab.js
import React, { useMemo } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as api from '../../services/api';
import { notify } from '../../services/notificationService';
import { useAuth } from '../../contexts/AuthContext';
import { triggerBrowserDownload } from '../../utils/fileDownloadHelper';

// UI Components
import {
  FiDownload,
  FiUnlock,
  FiInfo,
  FiZap,
  FiFileText,
} from 'react-icons/fi';
import Alert from '../../components/ui/Alert/Alert';
import Button from '../../components/ui/Button/Button';
import Icon from '../../components/ui/Icon/Icon';
import Spinner from '../../components/ui/Spinner/Spinner';
import Stack, { HStack } from '../../components/ui/Stack/Stack';
import Table from '../../components/ui/Table/Table';
import Typography from '../../components/ui/Typography/Typography';
import EmptyState from '../../components/ui/EmptyState/EmptyState';
import LinkageStatistics from '../../components/userDashboard/LinkageStatistics';
import Badge from '../../components/ui/Badge/Badge';
import Card from '../../components/ui/Card/Card';
import Box from '../../components/ui/Box/Box';

const ResultsTab = () => {
  const { job, isRefetching } = useOutletContext();
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();

  const decryptAndDownloadMutation = useMutation({
    mutationFn: async (fileId) => {
      notify.info('Decrypting file... This may take a moment.');
      const decryptResponse = await api.decryptFile(job.project_id, fileId);

      notify.success('File decrypted. Preparing download...');
      const { decrypted_output_file_id, decrypted_output_file } =
        decryptResponse;

      if (!decrypted_output_file_id || !decrypted_output_file) {
        throw new Error(
          'Decryption response did not provide file details for download.',
        );
      }

      return api.downloadDecryptedFile(
        job.project_id,
        decrypted_output_file_id,
        decrypted_output_file, // Pass new filename
      );
    },
    onSuccess: (downloadData) => {
      if (downloadData.download_url) {
        triggerBrowserDownload(
          downloadData.download_url,
          downloadData.suggested_filename,
        );
      } else if (downloadData.fileBlob) {
        triggerBrowserDownload(downloadData.fileBlob, downloadData.filename);
      } else {
        notify.error('Could not download file: Invalid response from server.');
        return;
      }
      notify.success('Decrypt and download process initiated.');
      queryClient.invalidateQueries({ queryKey: ['jobDetails', job.id] });
    },
    onError: (error) => {
      notify.error(error.message || 'The decrypt and download process failed.');
    },
  });

  const downloadMutation = useMutation({
    mutationFn: ({ fileId, filename }) =>
      api.downloadDecryptedFile(job.project_id, fileId, filename),
    onSuccess: (downloadData) => {
      if (downloadData.download_url) {
        triggerBrowserDownload(
          downloadData.download_url,
          downloadData.suggested_filename,
        );
      } else if (downloadData.fileBlob) {
        triggerBrowserDownload(downloadData.fileBlob, downloadData.filename);
      } else {
        notify.error('Could not download file: Invalid response from server.');
        return;
      }
      notify.success('Download initiated!');
    },
    onError: (error) => notify.error(error.message || 'Download failed.'),
  });

  const downloadReportMutation = useMutation({
    mutationFn: ({ fileId, filename }) =>
      api.downloadLinkageReport(job.project_id, fileId, filename),
    onSuccess: (downloadData) => {
      if (downloadData.download_url) {
        triggerBrowserDownload(
          downloadData.download_url,
          downloadData.suggested_filename,
        );
      } else if (downloadData.fileBlob) {
        triggerBrowserDownload(downloadData.fileBlob, downloadData.filename);
      } else {
        notify.error(
          'Could not download report: Invalid response from server.',
        );
        return;
      }
      notify.success('Report download initiated.');
    },
    onError: (error) =>
      notify.error(error.message || 'Report download failed.'),
  });

  const outputFiles = useMemo(() => {
    if (!job?.files) return [];
    return job.files
      .filter(
        (f) =>
          f.file_type === 'linkage_results' ||
          f.file_type === 'linkage_reports' ||
          f.file_type === 'decrypted_output',
      )
      .sort((a, b) => new Date(b.processed_at) - new Date(a.processed_at));
  }, [job]);

  const columns = useMemo(
    () => [
      {
        Header: 'Filename',
        accessor: 'filename_on_disk',
        Cell: ({ row: file }) => (
          <Stack spacing="xs">
            <Typography component="code" variant="body2">
              {file.filename_on_disk}
            </Typography>
            {file.file_type === 'decrypted_output' &&
              file.file_metadata?.source_filename_on_disk && (
                <Typography variant="caption" color="placeholder">
                  (Decrypted from: {file.file_metadata.source_filename_on_disk})
                </Typography>
              )}
          </Stack>
        ),
      },
      {
        Header: 'Type',
        accessor: 'file_type',
        width: 200,
        Cell: ({ value }) => (
          <Badge variant="default" icon={<Icon as={FiFileText} />}>
            {value.replace(/_/g, ' ')}
          </Badge>
        ),
      },
      {
        Header: 'Processed At',
        accessor: 'processed_at',
        width: 220,
        Cell: ({ value }) => new Date(value).toLocaleString(),
      },
      {
        Header: 'Actions',
        id: 'actions',
        width: 220,
        Cell: ({ row: file }) => {
          const canDownloadReport = hasPermission('file:download:report');
          const canDecrypt = hasPermission('file:decrypt');
          const canDownloadDecrypted = hasPermission('file:download:decrypted');

          switch (file.file_type) {
            case 'linkage_reports':
              return canDownloadReport ? (
                <Button
                  size="sm"
                  leftIcon={<Icon as={FiDownload} />}
                  onClick={() =>
                    downloadReportMutation.mutate({
                      fileId: file.id,
                      filename: file.filename_on_disk,
                    })
                  }
                  isLoading={
                    downloadReportMutation.isPending &&
                    downloadReportMutation.variables?.fileId === file.id
                  }
                >
                  Download Report
                </Button>
              ) : null;
            case 'linkage_results':
              return canDecrypt ? (
                <Button
                  size="sm"
                  variant="primary"
                  leftIcon={<Icon as={FiUnlock} />}
                  onClick={() => decryptAndDownloadMutation.mutate(file.id)}
                  isLoading={
                    decryptAndDownloadMutation.isPending &&
                    decryptAndDownloadMutation.variables === file.id
                  }
                >
                  Decrypt & Download
                </Button>
              ) : null;
            case 'decrypted_output':
              return canDownloadDecrypted ? (
                <Button
                  size="sm"
                  leftIcon={<Icon as={FiDownload} />}
                  onClick={() =>
                    downloadMutation.mutate({
                      fileId: file.id,
                      filename: file.filename_on_disk,
                    })
                  }
                  isLoading={
                    downloadMutation.isPending &&
                    downloadMutation.variables?.fileId === file.id
                  }
                >
                  Download Again
                </Button>
              ) : null;
            default:
              return (
                <Typography variant="caption" color="placeholder">
                  -
                </Typography>
              );
          }
        },
      },
    ],
    [
      hasPermission,
      decryptAndDownloadMutation,
      downloadMutation,
      downloadReportMutation,
    ],
  );

  if (!job) return <Spinner />;
  const hasResults = outputFiles.length > 0;
  const canRerun = hasPermission('job:finalize');
  const stats = job?.latest_linkage_statistics;

  if (!hasResults) {
    return (
      <EmptyState
        icon={FiInfo}
        title="Results Pending"
        message="Once the reconciliation process is successfully run and completed, the output files and statistics will appear here."
      />
    );
  }

  return (
    <Stack spacing="lg">
      {isRefetching && (
        <Alert variant="info" icon={<Spinner size="sm" />}>
          Checking for updates...
        </Alert>
      )}
      <Typography variant="body1" color="secondary">
        Review the statistics and download the outputs for this job.
      </Typography>
      {stats ? (
        <LinkageStatistics statistics={stats} />
      ) : (
        <Alert variant="info" mt="md">
          This job has completed, but no summary statistics were generated.
        </Alert>
      )}
      <Table
        title="Output Files"
        titleSrOnly
        columns={columns}
        data={outputFiles}
        containerProps={{ mt: 'lg' }}
        emptyMessage="This should not be seen if hasResults is true."
      />
      {canRerun && (job.status === 'completed' || job.status === 'failed') && (
        <Card
          mt="lg"
          elevation="none"
          style={{ backgroundColor: 'var(--cipher-color-accent-info-light)' }}
        >
          <HStack p="md" justifyContent="space-between" alignItems="center">
            <Typography>
              Need to generate a new version with updated data?
            </Typography>
            <Link to={`/user/workspace/${job.id}/run`}>
              <Button variant="secondary" leftIcon={<Icon as={FiZap} />}>
                Go to Re-run Linkage
              </Button>
            </Link>
          </HStack>
        </Card>
      )}
    </Stack>
  );
};

export default ResultsTab;
